/*
 * Campaign selector identity — one real governed campaign, one selectable row.
 *
 * The canonical resolver already merges live, imported and governed records
 * into one option per campaign. What it never saw was the row the page adds
 * afterwards in JSX:
 *
 *     <option value="auto">{autoCampaignOptionLabel}</option>
 *
 * That row is a MODE, not a campaign — but its label is derived from a
 * campaign's name, and the page then stripped the "Auto — " prefix that was the
 * only thing distinguishing it. So it rendered as a bare campaign name, sitting
 * outside every identity and dedupe rule the resolver enforces.
 *
 * While the selector had no other options that was invisible: the synthetic row
 * WAS the only row. Once governed campaign discovery was wired, the same
 * advertiser rendered its campaign twice — "Never Stop Chasing" (the mode) above
 * "Never Stop Chasing" (campaign 44) — and Paper Flowers, which has exactly one
 * campaign, rendered "2026 Theatrical Release" twice.
 *
 * The rule this module enforces:
 *
 *   identity, never label. Two records collapse to one row when they resolve to
 *   the same immutable identity, and stay two rows when they do not — however
 *   alike they are named.
 *
 * Identity hierarchy, most authoritative first:
 *
 *   campaign:<mediaCampaignId>        a governed internal campaign
 *   reporting-only:<id>               an imported reporting record with no
 *                                     governed campaign behind it
 *   provider:<provider>:<externalId>  an ad-platform record, namespaced so two
 *                                     providers cannot collide on an id
 *   option:<id>                       last resort, still an id — never a name
 *
 * A synthetic row is legitimate only where no real campaign represents the same
 * context. The moment the governed campaign it stands for is present, the
 * governed campaign wins and the synthetic row is suppressed — and the
 * selection is upgraded to that campaign's own identity, so the mode row
 * disappears because it was resolved, not because it was hidden.
 *
 * This is the contract Campaign Optimizer already keeps ("the prompt row exists
 * only while nothing is selected; governed auto-selection persists the
 * campaign's own id"). It lives here so both selectors, in both portals, keep
 * one contract instead of two implementations of the same intent.
 */

export const CAMPAIGN_SELECTOR_AUTO_VALUE = "auto";

export const CAMPAIGN_OPTION_SOURCE = Object.freeze({
  GOVERNED: "governed",
  REPORTING: "reporting",
  PROVIDER: "provider",
  SYNTHETIC: "synthetic",
});

const text = (value) => String(value ?? "").trim();

const stripReportingPrefix = (value) => text(value).replace(/^reporting:/i, "");

export const classifyCampaignOptionSource = (option = {}) => {
  if (!option || typeof option !== "object") return CAMPAIGN_OPTION_SOURCE.PROVIDER;
  if (option.synthetic === true) return CAMPAIGN_OPTION_SOURCE.SYNTHETIC;
  if (text(option.mediaCampaignId)) return CAMPAIGN_OPTION_SOURCE.GOVERNED;
  if (option.reportingOnly === true) return CAMPAIGN_OPTION_SOURCE.REPORTING;
  return CAMPAIGN_OPTION_SOURCE.PROVIDER;
};

/*
 * The immutable key for one campaign option.
 *
 * Governed identity outranks every provider alias, so a campaign that a
 * platform also knows about collapses onto its governed row rather than
 * appearing twice. Nothing here reads a display name — a campaign renamed
 * yesterday keys the same as it did the day before, and two campaigns named
 * alike key differently.
 */
export const resolveCampaignOptionIdentity = (option = {}) => {
  if (!option || typeof option !== "object") return "";

  if (option.synthetic === true) {
    return `synthetic:${text(option.id) || CAMPAIGN_SELECTOR_AUTO_VALUE}`;
  }

  const governed = text(option.mediaCampaignId);
  if (governed) return `campaign:${governed}`;

  if (option.reportingOnly === true) {
    const reporting = stripReportingPrefix(option.reportingCampaignId || option.id);
    if (reporting) return `reporting-only:${reporting}`;
  }

  const external = text(option.externalCampaignId) || text(option.liveCampaignId);
  if (external) return `provider:${text(option.providerKey) || "meta"}:${external}`;

  const id = stripReportingPrefix(option.id);
  return id ? `option:${id}` : "";
};

/*
 * Collapse records that resolve to the same identity, keeping the first — which,
 * because ordering is resolved before this runs, is the canonical one. Records
 * that merely LOOK alike are untouched.
 */
export const dedupeCampaignOptionsByIdentity = (options = []) => {
  const seen = new Set();
  return (Array.isArray(options) ? options : [])
    .filter((option) => option && typeof option === "object")
    .filter((option) => {
      const identity = resolveCampaignOptionIdentity(option);
      if (!identity || seen.has(identity)) return false;
      seen.add(identity);
      return true;
    });
};

/*
 * The normalized rows a selector renders. Every consumer receives identity,
 * campaign id, label and source already resolved, so no portal has to do its
 * own cleanup at the <select>.
 */
export const buildCampaignSelectorRows = ({ options = [] } = {}) =>
  dedupeCampaignOptionsByIdentity(options)
    .map((option) => {
      const identity = resolveCampaignOptionIdentity(option);
      const value = text(option.id);
      const label = text(option.name) || text(option.displayName);
      const governed = text(option.mediaCampaignId);
      return {
        identity,
        value,
        label,
        campaignId: governed || null,
        source: classifyCampaignOptionSource(option),
        status: text(option.status) || "",
        option,
      };
    })
    .filter((row) => row.identity && row.value && row.label);

/*
 * The governed campaign the synthetic "auto" row currently stands for, resolved
 * against the rows the selector is actually offering.
 *
 * Resolution is by IDENTITY against a row already in the list — the auto-detected
 * campaign IS one of these objects, and the approved plan is matched on its
 * governed campaign id. A name is never compared, so a shared prefix or a
 * duplicate title can neither create nor prevent an upgrade. Returns null when
 * the mode genuinely stands for nothing on offer, which is when the synthetic
 * row is legitimate and must survive.
 */
export const resolveGovernedSelectionUpgrade = ({
  selectedCampaignId = null,
  rows = [],
  autoDetectedCampaign = null,
  approvedPlanCampaign = null,
} = {}) => {
  if (text(selectedCampaignId) !== CAMPAIGN_SELECTOR_AUTO_VALUE) return null;
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;

  const detectedIdentity = resolveCampaignOptionIdentity(autoDetectedCampaign || {});
  if (detectedIdentity) {
    const match = list.find((row) => row.identity === detectedIdentity);
    if (match) return match.value;
  }

  const planCampaignId = text(
    approvedPlanCampaign?.mediaCampaignId ??
      approvedPlanCampaign?.id ??
      approvedPlanCampaign?.campaign_id
  );
  if (planCampaignId) {
    const match = list.find((row) => row.identity === `campaign:${planCampaignId}`);
    if (match) return match.value;
  }

  return null;
};

/*
 * The synthetic row renders only while nothing real is selected AND it stands
 * for nothing the list already offers. Both conditions matter: the first keeps
 * it from sitting beside a campaign the operator chose, the second keeps it
 * from sitting beside the campaign it resolved to.
 */
export const shouldRenderAutoCampaignOption = ({
  selectedCampaignId = null,
  governedSelectionUpgrade = null,
} = {}) =>
  text(selectedCampaignId) === CAMPAIGN_SELECTOR_AUTO_VALUE && !governedSelectionUpgrade;
